import { useClientTranslation } from '@/lib/core/i18n';
import { Checkbox } from '@/components/ui/checkbox';

interface ConsentBlockProps {
  /** Row key, so the checkbox ids stay unique across the expanded panels. */
  idPrefix: string;
  termsAccepted: boolean;
  onTermsChange: (accepted: boolean) => void;
  marketingOptIn: boolean;
  onMarketingChange: (optIn: boolean) => void;
  /** Resolved from `appSettings.properties.accountTermsConditions`; the link is dropped if absent. */
  termsHref?: string;
}

/**
 * The consent pair shown inside every expanded payment panel (Figma: Checkout | Payment methods -
 * credit card filled / apple pay-google pay selected / mbway selected).
 *
 * Terms starts unchecked even though the design renders it ticked: the CTA is gated on it, and
 * pre-accepting terms on the customer's behalf is not consent. The design's ticked state is what
 * the row looks like *after* the customer accepts.
 *
 * The marketing box has no destination — there is no consent field anywhere in the generated API
 * model, and no preferences endpoint covers it. It is rendered and its state is tracked so the
 * screen matches the design and so wiring it up later is a one-line change, but nothing is
 * currently sent. Do not present it to the customer as having been saved.
 */
export function ConsentBlock({
  idPrefix,
  termsAccepted,
  onTermsChange,
  marketingOptIn,
  onMarketingChange,
  termsHref,
}: ConsentBlockProps) {
  const t = useClientTranslation();
  const termsId = `${idPrefix}-terms`;
  const marketingId = `${idPrefix}-marketing`;
  const termsLabel = t('App.Checkout.TermsAndConditions');

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <Checkbox
          id={termsId}
          checked={termsAccepted}
          onCheckedChange={checked => onTermsChange(checked === true)}
          className="mt-px"
        />
        {/*
          The sentence is split across two keys rather than interpolated: `useClientTranslation`
          returns a string, so a `{link}` placeholder cannot carry an anchor element. Both locales
          put the link at the end of the clause, so a prefix + link reads correctly in each.
        */}
        <label
          htmlFor={termsId}
          className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] leading-[18px] tracking-[0.5px]"
        >
          {t('App.Checkout.AgreeToTerms')}{' '}
          {termsHref ? (
            <a
              href={termsHref}
              target="_blank"
              rel="noreferrer"
              className="font-semibold underline"
              onClick={event => event.stopPropagation()}
            >
              {termsLabel}
            </a>
          ) : (
            <span className="font-semibold underline">{termsLabel}</span>
          )}
          *
        </label>
      </div>

      <div className="flex items-start gap-3">
        <Checkbox
          id={marketingId}
          checked={marketingOptIn}
          onCheckedChange={checked => onMarketingChange(checked === true)}
          className="mt-px"
        />
        <label
          htmlFor={marketingId}
          className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[12px] leading-[18px] tracking-[0.5px]"
        >
          {t('App.Checkout.MarketingOptIn')}
        </label>
      </div>
    </div>
  );
}
